import { useCallback, useEffect, useState } from 'react';
import { appUrl } from './lib/appBase.js';
import { startEmulator, EMULATOR_PLAYER, shutdownEmulator } from './lib/emulator.js';
import { loadRomForEmulatorJs } from './lib/romLoader.js';
import { resolveRomUrl } from './lib/romApi.js';
import { isUnsupportedRom, UNSUPPORTED_EXTS, fileExtLower } from './lib/romUtils.js';
import { downloadVaultRom } from './lib/vaultApi.js';
import {
  externalEmulatorConfig,
  externalEmulatorForExt,
  isExternalEmulatorKey,
  launchExternalSession,
} from './lib/externalEmulators.js';
import { SYSTEMS, STORAGE_KEY } from './lib/systems.js';
import { useRomLibrary } from './hooks/useRomLibrary.js';
import { useFavorites } from './hooks/useFavorites.js';
import { useGamepadStatus } from './hooks/useGamepadStatus.js';
import { useProfileSync } from './hooks/useProfileSync.js';
import { LibraryPage } from './components/LibraryPage.jsx';
import { PlayScreen } from './components/PlayScreen.jsx';
import { ExternalPlayScreen } from './components/ExternalPlayScreen.jsx';
import { initDeviceLayout } from './lib/deviceLayout.js';
import './styles/arcade.css';

initDeviceLayout();

function readSavedSystem() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && SYSTEMS[saved]) return saved;
  } catch {
    /* ignore */
  }
  return 'nes';
}

export function RetroLibrary({ onExit }) {
  const [view, setView] = useState('library');
  const [systemKey, setSystemKey] = useState(readSavedSystem);
  const [searchTerm, setSearchTerm] = useState('');
  const [romStatus, setRomStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState('A carregar…');
  const [playingTitle, setPlayingTitle] = useState('');
  const [pendingLaunch, setPendingLaunch] = useState(null);
  const [externalSession, setExternalSession] = useState(null);
  const [downloadingVaultId, setDownloadingVaultId] = useState(null);
  const [playCore, setPlayCore] = useState(null);

  const { groups, loading: libraryLoading, refresh: refreshLibrary } = useRomLibrary(searchTerm);
  const { favoriteGames, addPlayedGame } = useFavorites(groups, searchTerm);
  const { text: padStatus, count: padCount, refresh: refreshPads } = useGamepadStatus();
  const { profileStatus } = useProfileSync();

  const rememberSystem = useCallback((key) => {
    try {
      localStorage.setItem(STORAGE_KEY, key);
    } catch {
      /* ignore */
    }
  }, []);

  const runEmulator = useCallback(async (core, gameName, gameUrl, gameForFavorite) => {
    setLoading(true);
    setLoadingText(`A carregar\n${gameName}`);
    setRomStatus(`A carregar… ${gameName}`);
    try {
      const romSource = await loadRomForEmulatorJs(
        gameForFavorite?.filename || gameName,
        gameUrl,
      );
      setPlayCore(core);
      await startEmulator({
        playerSelector: EMULATOR_PLAYER,
        core,
        gameName: gameForFavorite?.filename || gameName,
        gameUrl: romSource,
      });
      setLoading(false);
      if (gameForFavorite?.id && gameForFavorite?.url) {
        addPlayedGame(gameForFavorite);
      }
      setRomStatus(core === 'nds' ? 'Toca em «Iniciar jogo» no ecrã.' : '');
    } catch (err) {
      console.error(err);
      setLoading(false);
      setRomStatus('Erro ao carregar a ROM. Verifica se o ficheiro existe e se estás autenticado.');
    }
  }, [addPlayedGame]);

  useEffect(() => {
    if (view !== 'play' || !pendingLaunch) return;
    const { core, gameName, gameUrl, game } = pendingLaunch;
    setPendingLaunch(null);
    void runEmulator(core, gameName, gameUrl, game);
  }, [view, pendingLaunch, runEmulator]);

  const launchExternal = useCallback(async (game, file = null) => {
    const platform = game.ejsKey;
    const cfg = externalEmulatorConfig(platform);
    if (!cfg) return;

    setPlayingTitle(game.title || file?.name || game.title);
    setView('external');

    await launchExternalSession({
      platform,
      title: game.title || file?.name,
      filename: game.filename,
      file,
      onSession: (session) => setExternalSession(session),
      onPlayed: () => addPlayedGame(game),
    });
  }, [addPlayedGame]);

  const launchGame = useCallback(
    (game, core) => {
      const { filename, title, url } = game;
      const decoded = decodeURIComponent(String(filename || title).replace(/\+/g, ' '));
      if (isUnsupportedRom(decoded)) {
        setRomStatus(`${UNSUPPORTED_EXTS[fileExtLower(decoded)]} não suportado: ${decoded}`);
        return;
      }
      const resolvedCore = SYSTEMS[core] ? core : systemKey;
      if (resolvedCore !== systemKey) {
        setSystemKey(resolvedCore);
        rememberSystem(resolvedCore);
      }
      const gameUrl = resolveRomUrl(url, filename);
      setPlayingTitle(title || decoded);
      setPendingLaunch({
        core: resolvedCore,
        gameName: title || decoded,
        gameUrl,
        game,
      });
      setView('play');
    },
    [rememberSystem, systemKey],
  );

  const handleSelectGame = useCallback(
    (game) => {
      if (game.unsupported) {
        setRomStatus(`${game.unsupportedLabel} não suportado: ${game.title}`);
        return;
      }
      if (game.needsDownload || !game.url) {
        setRomStatus(`«${game.title}» ainda não tem ROM — clica em ↓ para baixar.`);
        return;
      }
      if (isExternalEmulatorKey(game.ejsKey)) {
        void launchExternal(game);
        return;
      }
      const core = game.ejsKey && SYSTEMS[game.ejsKey] ? game.ejsKey : systemKey;
      launchGame(game, core);
    },
    [launchGame, launchExternal, systemKey],
  );

  const handleDownloadRom = useCallback(
    async (game) => {
      if (!game.vaultId || downloadingVaultId) return;
      setDownloadingVaultId(game.vaultId);
      setRomStatus(`A baixar «${game.title}»…`);
      try {
        const result = await downloadVaultRom(appUrl, game.vaultId);
        await refreshLibrary();
        setRomStatus(
          result.alreadyHad
            ? `ROM já existia: ${result.title}`
            : `ROM baixada: ${result.title} — clica para jogar.`,
        );
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setRomStatus(
          /\b429\b/.test(msg) || /limitou pedidos/i.test(msg)
            ? 'Vimm.net ocupado — espera 1–2 minutos e tenta ↓ outra vez.'
            : `Erro ao baixar: ${msg}`,
        );
      } finally {
        setDownloadingVaultId(null);
      }
    },
    [downloadingVaultId, refreshLibrary],
  );

  const handleLoadFile = useCallback(
    (file, errMsg) => {
      if (errMsg) {
        setRomStatus(errMsg);
        return;
      }
      if (!file) return;

      const extPlatform = externalEmulatorForExt(file.name);
      if (extPlatform) {
        setPlayingTitle(file.name);
        void launchExternal(
          {
            id: `upload-${file.name}`,
            title: file.name,
            filename: file.name,
            ejsKey: extPlatform,
            url: null,
          },
          file,
        );
        return;
      }

      setPlayingTitle(file.name);
      setPendingLaunch({ core: systemKey, gameName: file.name, gameUrl: file, game: null });
      setView('play');
    },
    [systemKey, launchExternal],
  );

  const handleBack = useCallback(async () => {
    if (view === 'play') {
      await shutdownEmulator(EMULATOR_PLAYER);
    }
    setView('library');
    setRomStatus('');
    setPlayingTitle('');
    setExternalSession(null);
    setLoading(false);
  }, [view]);

  return (
    <div
      className={`app-shell${view === 'play' ? ' app-shell--play' : ' app-shell--library'}`}
    >
      {view === 'library' ? (
        <LibraryPage
          groups={groups}
          libraryLoading={libraryLoading}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          favoriteGames={favoriteGames}
          onSelectGame={handleSelectGame}
          onDownloadRom={handleDownloadRom}
          downloadingVaultId={downloadingVaultId}
          onLoadFile={handleLoadFile}
          padStatus={padStatus}
          padCount={padCount}
          onWakeGamepad={refreshPads}
          profileStatus={profileStatus}
          romStatus={romStatus}
          onExit={onExit}
        />
      ) : view === 'external' && externalSession ? (
        <ExternalPlayScreen
          title={externalSession.title}
          emulatorName={externalSession.emulatorName}
          platformLabel={externalSession.platformLabel}
          iframeUrl={externalSession.iframeUrl}
          romFilename={externalSession.filename}
          launchStatus={externalSession.launchStatus}
          onBack={() => void handleBack()}
        />
      ) : (
        <PlayScreen
          loading={loading}
          loadingText={loadingText}
          needsManualStart={playCore === 'nds'}
          onBack={() => void handleBack()}
        />
      )}
    </div>
  );
}
